import { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getClients } from '@services/clients.service';
import { getProjectTemplates, getPublicCategories } from '@services/projects.service';
import { PROJECT_CLIENT_TYPE } from '@/constants/projectClientType';
import { useCreateProject } from './useCreateProject';
import { budgetFromFormValue } from './projectHelpers';
import { PROJECTS_QUERY_KEY } from './useProjects';
import { formatUnderscoreToSpace } from '@/utils/formatText';

const CREATE_PROJECT_DRAFT_KEY = 'code360_create_project_draft_v2';

export const PROJECT_CREATE_CLIENT_TAB = {
    MY_SPRINTO: 'my_sprinto',
    SPRINTO_EMAIL: 'sprinto_email',
    OUTSIDE: 'outside',
    IN_HOUSE: 'in_house',
};

export const PROJECT_TYPE_OPTIONS = [
    { value: 'fixed_sdlc', label: 'Fixed SDLC' },
    { value: 'website_design', label: 'Website Design' },
    { value: 'hourly_resource_augment', label: 'Hourly Resource Augment' },
    { value: 'fixed_recurring_resource_augment', label: 'Fixed Recurring Resource Augment' },
    { value: 'hourly_project_milestones', label: 'Hourly Project Milestones' },
    { value: 'in_house_internal', label: 'In House Internal' },
];

export const PRIORITY_OPTIONS = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

export const PROJECT_TEMPLATES_QUERY_KEY = ['project-templates'];
export const PUBLIC_CATEGORIES_QUERY_KEY = ['public-categories'];

/**
 * 3 Steps:
 * 0: Template Selection
 * 1: Project Details (Name, Type, Category, Budget, Priority)
 * 2: Client Selection
 */
export function getRequiredModulesForTemplate(template, isManagedClient, projectType) {
    const result = {
        isQA: { value: false, locked: false, allowed: false },
        isPMO: { value: false, locked: false, allowed: false },
        isBlockers: { value: false, locked: false, allowed: false },
        isCollaboration: { value: false, locked: false, allowed: false },
        isBacklogs: { value: false, locked: false, allowed: false },
        isChangeRequest: { value: false, locked: false, allowed: false },
        isReviews: { value: false, locked: false, allowed: false }
    };

    if (!template) {
        return result;
    }

    const isAugment = projectType === 'hourly_resource_augment' || projectType === 'fixed_recurring_resource_augment';
    const isInHouse = projectType === 'in_house_internal';
    const hasDeliveryWork = !!(template.usePhases || template.useMilestones || template.useSprints || template.useTasks);
    const hasSubmission = template.submissionMode !== 'none';
    const isMemoSubmissionPath = template.submissionMode === 'memo' || !!template.useSubmissionMemo;
    const clientScoped = !!isManagedClient;

    // 1. isQA
    const qaAllowed = !!(template.useTasks && hasSubmission && !isMemoSubmissionPath && (template.submissionMode === 'formal' || template.submissionMode === 'internal'));
    if (qaAllowed) {
        result.isQA = { value: true, locked: false, allowed: true };
    } else {
        result.isQA = { value: false, locked: true, allowed: false };
    }

    // 2. isPMO
    const pmoAllowed = !!(hasSubmission && hasDeliveryWork && !isMemoSubmissionPath && template.submissionMode === 'formal');
    if (pmoAllowed) {
        result.isPMO = { value: true, locked: false, allowed: true };
    } else {
        result.isPMO = { value: false, locked: true, allowed: false };
    }

    // 3. isBlockers
    const blockersAllowed = !!template.useTasks;
    if (blockersAllowed) {
        result.isBlockers = { value: true, locked: false, allowed: true };
    } else {
        result.isBlockers = { value: false, locked: true, allowed: false };
    }

    // 4. isCollaboration
    const collabAllowed = !!(hasDeliveryWork && !isAugment);
    if (collabAllowed) {
        result.isCollaboration = { value: true, locked: false, allowed: true };
    } else {
        result.isCollaboration = { value: false, locked: true, allowed: false };
    }

    // 5. isBacklogs
    const backlogsAllowed = !!(clientScoped && template.useTasks);
    if (backlogsAllowed) {
        result.isBacklogs = { value: true, locked: false, allowed: true };
    } else {
        result.isBacklogs = { value: false, locked: true, allowed: false };
    }

    // 6. isChangeRequest
    const clientSubmissionForApproval = !isAugment && !isInHouse && (template.submissionMode === 'formal' || template.submissionMode === 'memo');
    const changeRequestAllowed = !!(clientScoped && clientSubmissionForApproval);
    if (changeRequestAllowed) {
        result.isChangeRequest = { value: true, locked: false, allowed: true };
    } else {
        result.isChangeRequest = { value: false, locked: true, allowed: false };
    }

    // 7. isReviews
    const reviewsAllowed = !!(clientScoped && hasDeliveryWork && !isAugment);
    if (reviewsAllowed) {
        result.isReviews = { value: true, locked: false, allowed: true };
    } else {
        result.isReviews = { value: false, locked: true, allowed: false };
    }

    return result;
}

export const useCreateProjectModal = ({
    onSuccess,
    onCancel,
    projectRequestId,
    defaultClientTab = PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO,
} = {}) => {
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const projectType = Form.useWatch('projectType', form);
    
    // ── Persistence / Draft ──────────────────────────────────────────────
    const [step, setStep] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState(undefined);
    const [clientTab, setClientTab] = useState(defaultClientTab);
    const [clientSearchText, setClientSearchText] = useState('');
    const [clientDropdownOpened, setClientDropdownOpened] = useState(false);

    // ── Module Feature Toggles ────────────────────────────────────────────
    const DEFAULT_MODULE_TOGGLES = {
        isQA: false,
        isPMO: false,
        isBlockers: false,
        isCollaboration: false,
        isBacklogs: false,
        isChangeRequest: false,
        isReviews: false,
    };
    const [moduleToggles, setModuleToggles] = useState(DEFAULT_MODULE_TOGGLES);

    // Initialize from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem(CREATE_PROJECT_DRAFT_KEY);
        if (saved) {
            try {
                const { step: s, formValues, selectedTemplate: st, clientTab: ct, moduleToggles: mt } = JSON.parse(saved);
                setStep(s ?? 0);
                setSelectedTemplate(st !== undefined ? st : undefined);
                setClientTab(ct || defaultClientTab);
                if (mt) setModuleToggles({ ...DEFAULT_MODULE_TOGGLES, ...mt });
                if (formValues) form.setFieldsValue(formValues);
            } catch (e) {
                console.error("Failed to load project draft", e);
            }
        }
    }, [form, defaultClientTab]);



    const saveDraft = useCallback(() => {
        const values = form.getFieldsValue();
        const draft = {
            step,
            formValues: values,
            selectedTemplate,
            clientTab,
            moduleToggles,
        };
        localStorage.setItem(CREATE_PROJECT_DRAFT_KEY, JSON.stringify(draft));
    }, [step, selectedTemplate, clientTab, moduleToggles, form]);

    // ── Templates (Step 0) ────────────────────────────────────────────────
    const templatesQuery = useQuery({
        queryKey: PROJECT_TEMPLATES_QUERY_KEY,
        queryFn: getProjectTemplates,
        enabled: step === 0, 
        staleTime: 5 * 60_000,
    });
    // Normalize: cache may hold { data: [], total } (from useProjectTemplates) or a plain array
    const templates = useMemo(() => {
        const raw = templatesQuery.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    }, [templatesQuery.data]);

    // ── Categories (Step 1) ───────────────────────────────────────────────
    const categoriesQuery = useQuery({
        queryKey: PUBLIC_CATEGORIES_QUERY_KEY,
        queryFn: getPublicCategories,
        enabled: step === 1,
        staleTime: 10 * 60_000,
    });
    const categories = categoriesQuery.data ?? [];
    const categoryOptions = useMemo(() => categories.map(c => ({ value: c.id, label: c.name })), [categories]);

    // ── Clients (Step 2) ──────────────────────────────────────────────────
    const clientsQuery = useQuery({
        queryKey: ['clients', 'create-project', { q: clientSearchText }],
        queryFn: () =>
            getClients({ page: 1, limit: 100, sortBy: 'createdAt', sortOrder: 'desc', q: clientSearchText || undefined, type: 'all' }),
        enabled: step === 2 && clientDropdownOpened && clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO,
        staleTime: 30_000,
    });
    const clients = clientsQuery.data?.data ?? [];
    const clientOptions = useMemo(() => clients.map((c) => {
        const name = c?.name ?? '';
        const email = c?.email ?? '';
        const base = name ? (email ? `${name} (${email})` : name) : email || c?.id;
        return { value: c?.id, label: base };
    }), [clients]);
    const selectedClientId = Form.useWatch('clientId', form);
    const isManagedClient = useMemo(() => {
        if (clientTab === PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL) {
            return true;
        }
        if (clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO) {
            if (!selectedClientId) {
                return true;
            }
            const foundClient = clients.find(c => c.id === selectedClientId || c.clientUserId === selectedClientId);
            return foundClient ? !!foundClient.clientUserId : true;
        }
        return false;
    }, [clientTab, selectedClientId, clients]);

    // Update modules dynamically when template, isManagedClient, or projectType changes
    useEffect(() => {
        const requiredMap = getRequiredModulesForTemplate(selectedTemplate, isManagedClient, projectType);
        setModuleToggles(prev => {
            const next = { ...prev };
            
            Object.keys(DEFAULT_MODULE_TOGGLES).forEach(key => {
                const config = requiredMap[key] || { value: false, locked: true, allowed: false };
                if (!config.allowed) {
                    next[key] = false;
                } else if (config.locked) {
                    next[key] = config.value;
                } else {
                    next[key] = prev[key] !== undefined ? prev[key] : config.value;
                }
            });

            return next;
        });
    }, [selectedTemplate, isManagedClient, projectType]);

    // ── Project Type Options (Dynamic based on Template) ────────────────
    const filteredProjectTypeOptions = useMemo(() => {
        if (!selectedTemplate || !selectedTemplate.allowedProjectTypes?.length) {
            return PROJECT_TYPE_OPTIONS;
        }

        return selectedTemplate.allowedProjectTypes.map(type => {
            const existing = PROJECT_TYPE_OPTIONS.find(opt => opt.value === type);
            if (existing) return existing;

            // Fallback for types not in hardcoded list
            const label = formatUnderscoreToSpace(type);
            return { value: type, label };
        });
    }, [selectedTemplate]);

    // ── Mutation ───────────────────────────────────────────────────────────
    const createProjectMutation = useCreateProject();

    // ── Navigation ────────────────────────────────────────────────────────
    const handleNext = useCallback(() => {
        if (step === 0) {
            setStep(1);
        } else if (step === 1) {
            form.validateFields(['name', 'projectType', 'priority']).then(() => {
                setStep(2);
            });
        }
        saveDraft();
    }, [step, form, saveDraft]);

    const handleBack = useCallback(() => {
        if (step > 0) {
            setStep(step - 1);
            saveDraft();
        }
    }, [step, saveDraft]);

    const handleTemplateSelect = useCallback((template) => {
        setSelectedTemplate(template);
        
        const allowed = template?.allowedProjectTypes ?? [];
        const currentType = form.getFieldValue('projectType');
        let nextType = currentType;

        // Auto-select if only one option exists, or clear if current is invalid
        if (allowed.length === 1) {
            nextType = allowed[0];
            form.setFieldsValue({ projectType: allowed[0] });
        } else if (allowed.length > 0 && !allowed.includes(currentType)) {
            nextType = undefined;
            form.setFieldsValue({ projectType: undefined });
        } else if (allowed.length === 0 && !template) {
            // Reverting to standard
        }

        // Reset module toggles to defaults when template changes
        const requiredMap = getRequiredModulesForTemplate(template, isManagedClient, nextType);
        const initialToggles = {};
        Object.keys(DEFAULT_MODULE_TOGGLES).forEach(key => {
            const config = requiredMap[key] || { value: false, locked: true, allowed: false };
            initialToggles[key] = config.allowed ? config.value : false;
        });
        setModuleToggles(initialToggles);

        saveDraft();
    }, [form, saveDraft, isManagedClient]);

    const handleReset = useCallback(() => {
        localStorage.removeItem(CREATE_PROJECT_DRAFT_KEY);
        form.resetFields();
        setStep(0);
        setSelectedTemplate(undefined);
        setClientTab(defaultClientTab);
        setClientSearchText('');
        setModuleToggles({ ...DEFAULT_MODULE_TOGGLES });
    }, [form, defaultClientTab]);

    const handleSubmit = useCallback(() => {
        let clientFields = [];
        if (clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO) clientFields = ['clientId'];
        if (clientTab === PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL) clientFields = ['sprintoClientEmail'];
        if (clientTab === PROJECT_CREATE_CLIENT_TAB.OUTSIDE) clientFields = ['clientName', 'clientEmail', 'clientPhone'];

        form.validateFields(clientFields)
            .then((values) => {
                const allValues = form.getFieldsValue(true);
                
                // Determine if final client is managed
                const finalManagedClient = clientTab === PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL || (
                    clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO && (() => {
                        const cid = allValues.clientId;
                        if (!cid) return false;
                        const found = clients.find(c => c.id === cid || c.clientUserId === cid);
                        return found ? !!found.clientUserId : false;
                    })()
                );
                
                const finalRequiredMap = getRequiredModulesForTemplate(selectedTemplate, finalManagedClient, allValues.projectType);
                const sanitizedModules = {};
                Object.keys(moduleToggles).forEach(key => {
                    const config = finalRequiredMap[key] || { value: false, locked: true, allowed: false };
                    sanitizedModules[key] = config.allowed ? moduleToggles[key] : false;
                });

                const payload = {
                    name: allValues.name,
                    projectType: allValues.projectType,
                    budget: budgetFromFormValue(allValues.budget),
                    priority: allValues.priority,
                    ...((selectedTemplate?.id || selectedTemplate?._id) && { projectTemplateId: selectedTemplate.id || selectedTemplate._id }),
                    ...(allValues.categoryId && { categoryId: allValues.categoryId }),
                    ...(projectRequestId && { projectRequestId }),
                    // Module feature toggles
                    ...sanitizedModules,
                };

                if (clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO) {
                    Object.assign(payload, { clientType: 'my past clients', clientId: values.clientId });
                } else if (clientTab === PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL) {
                    Object.assign(payload, { clientType: 'platform client', clientEmail: String(values.sprintoClientEmail ?? '').trim() });
                } else if (clientTab === PROJECT_CREATE_CLIENT_TAB.OUTSIDE) {
                    Object.assign(payload, {
                        clientType: 'out of the platform',
                        clientName: values.clientName,
                        clientEmail: values.clientEmail,
                        clientPhone: values.clientPhone,
                    });
                } else if (clientTab === PROJECT_CREATE_CLIENT_TAB.IN_HOUSE) {
                    Object.assign(payload, { clientType: 'own product' });
                }

                createProjectMutation.mutate(payload, {
                    onSuccess: (project) => {
                        message.success('Project created');
                        queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
                        handleReset();
                        onSuccess?.(project);
                        onCancel?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create project');
                    },
                });
            })
            .catch(() => {});
    }, [form, clientTab, selectedTemplate, projectRequestId, moduleToggles, clients, createProjectMutation, queryClient, handleReset, onSuccess, onCancel]);

    // Auto-select first template from backend if nothing is selected or if standard (null) was previously selected
    useEffect(() => {
        if (templates && templates.length > 0) {
            const hasValidSelection = templates.some(t => (t.id || t._id) === (selectedTemplate?.id || selectedTemplate?._id));
            if (!hasValidSelection) {
                const defaultTemplate = templates[0];
                setSelectedTemplate(defaultTemplate);
                
                const allowed = defaultTemplate?.allowedProjectTypes ?? [];
                let nextType = undefined;
                if (allowed.length === 1) {
                    nextType = allowed[0];
                    form.setFieldsValue({ projectType: allowed[0] });
                }
                
                const requiredMap = getRequiredModulesForTemplate(defaultTemplate, isManagedClient, nextType);
                setModuleToggles(prev => {
                    const next = { ...prev };
                    Object.keys(DEFAULT_MODULE_TOGGLES).forEach(key => {
                        const config = requiredMap[key] || { value: false, locked: true, allowed: false };
                        next[key] = config.allowed ? config.value : false;
                    });
                    return next;
                });
            }
        }
    }, [templates, selectedTemplate, isManagedClient, form]);

    // Auto-save draft whenever anything changes
    useEffect(() => {
        saveDraft();
    }, [step, selectedTemplate, clientTab, moduleToggles, saveDraft]);

    // Handler for toggling individual module switches
    const handleModuleToggle = useCallback((key, value) => {
        setModuleToggles(prev => ({ ...prev, [key]: value }));
    }, []);

    return {
        step,
        handleNext,
        handleBack,
        handleReset,
        templates,
        templatesLoading: templatesQuery.isLoading && step === 0,
        selectedTemplate,
        handleTemplateSelect,
        form,
        categoryOptions,
        categoriesLoading: categoriesQuery.isLoading && step === 1,
        clientTab,
        setClientTab,
        PROJECT_CREATE_CLIENT_TAB,
        clientOptions,
        clientsLoading: clientsQuery.isPending,
        setClientSearchText,
        setClientDropdownOpened,
        clientsLength: clients.length,
        handleSubmit,
        isPending: createProjectMutation.isPending,
        handleCancel: onCancel,
        PROJECT_TYPE_OPTIONS: filteredProjectTypeOptions,
        PRIORITY_OPTIONS,
        moduleToggles,
        handleModuleToggle,
        isManagedClient,
    };
};
